import BonnetidImportPanel from "./BonnetidImportPanel";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Skeleton,
  Table,
  Tabs,
  Toggle,
} from "stories/Admin";
import useFetch from "hooks/useFetch";
import styles from "./ExternalPage.module.scss";
import MyMasjidExternalTab from "./MyMasjidExternalTab";
import SearchField, { matchesSearch } from "./SearchField";
import BonnetidToolsPanel from "./BonnetidToolsPanel";
import BonnetidRawPanel from "./BonnetidRawPanel";
import SourceChangeDialog from "./SourceChangeDialog";
import SyncDetailsDialog from "./SyncDetailsDialog";
import {
  ExternalJamatRegistration,
  ExternalSyncLog,
  SourceChangeResult,
} from "./bonnetidTypes";

interface ExternalJamatStats {
  registeredMosques: number;
  activeRegistrations: number;
  totalBonnetidJamatDays: number;
  totalBonnetidJummahRows: number;
  totalAdminJamatDays: number;
}

interface ExternalApiStatus {
  apiConfigured: boolean;
  cronEnabled: boolean;
  cronSchedule: string;
  circuitBreakerOpen: boolean;
  circuitBreakerFailures: number;
  circuitBreakerOpenUntil: string | null;
}

interface ExternalMosqueSyncResult {
  registration: ExternalJamatRegistration;
  syncLog: ExternalSyncLog;
}

interface ExternalDashboard {
  stats: ExternalJamatStats;
  apiStatus: ExternalApiStatus;
  registrations: ExternalJamatRegistration[];
  recentSyncLogs: ExternalSyncLog[];
}

/** Fanene på siden (i URL-en som ?fane=…, så en fane kan lenkes til og overlever oppdatering). */
const TABS = [
  { value: "oversikt", label: "Oversikt" },
  { value: "moskeer", label: "Moskeer" },
  { value: "verktoy", label: "Hent og sjekk" },
  { value: "radata", label: "Rådata" },
  { value: "import", label: "Import" },
  { value: "logg", label: "Synklogg" },
  { value: "mymasjid", label: "My-Masjid" },
] as const;

type ExternalTab = (typeof TABS)[number]["value"];

const isExternalTab = (value: string | null): boolean =>
  TABS.some((t) => t.value === value);

/** Fanene som viser Bonnetid-oversikten (og trenger den lastet). */
const DASHBOARD_TABS: ExternalTab[] = [
  "oversikt",
  "moskeer",
  "verktoy",
  "logg",
];

const formatDateTime = (value: string): string => {
  try {
    return new Date(value).toLocaleString("nb-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const ExternalPage: React.FC = () => {
  const { execute } = useFetch();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [dashboard, setDashboard] = useState<ExternalDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [params, setParams] = useSearchParams();
  const activeTab: ExternalTab = isExternalTab(params.get("fane"))
    ? (params.get("fane") as ExternalTab)
    : "oversikt";
  const setActiveTab = (tab: ExternalTab) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("fane", tab);
        return next;
      },
      { replace: true },
    );
  const [search, setSearch] = useState("");
  // Siste manuelle synk per registrering (loggrad med detaljer), og loggraden som vises i «Hva synken hentet».
  const [lastRowLog, setLastRowLog] = useState<Record<number, number>>({});
  const [detailsLog, setDetailsLog] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const [sourceDialog, setSourceDialog] = useState<{
    registration: ExternalJamatRegistration;
    suggested?: string;
  } | null>(null);

  const fetchDashboard = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    const result = (await executeRef.current(
      "/api/v1/admin/external-jamat/dashboard",
      "GET",
    )) as ExternalDashboard | null;
    if (result) setDashboard(result);
    if (showLoading) setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await executeRef.current("/api/v1/admin/external-jamat/sync", "POST");
      await fetchDashboard(false);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleToggleActive = async (
    registration: ExternalJamatRegistration,
  ) => {
    setTogglingId(registration.id);
    try {
      await executeRef.current(
        `/api/v1/admin/external-jamat/registrations/${registration.id}/active`,
        "PATCH",
        { isActive: !registration.isActive },
      );
      await fetchDashboard(false);
    } finally {
      setTogglingId(null);
    }
  };

  const handleSyncRegistration = async (
    registration: ExternalJamatRegistration,
  ) => {
    setSyncingId(registration.id);
    try {
      const result = (await executeRef.current(
        `/api/v1/admin/external-jamat/registrations/${registration.id}/sync`,
        "POST",
      )) as ExternalMosqueSyncResult | null;

      if (result) {
        if (result.syncLog.hasDetails && result.syncLog.id) {
          setLastRowLog((current) => ({
            ...current,
            [registration.id]: result.syncLog.id,
          }));
        }
        setDashboard((current) => {
          if (!current) return current;
          return {
            ...current,
            registrations: current.registrations.map((reg) =>
              reg.id === result.registration.id ? result.registration : reg,
            ),
            recentSyncLogs: [result.syncLog, ...current.recentSyncLogs].slice(
              0,
              50,
            ),
          };
        });
      } else {
        await fetchDashboard(false);
      }
    } finally {
      setSyncingId(null);
    }
  };

  const openSourceChange = useCallback(
    (registration: ExternalJamatRegistration, suggested?: string) =>
      setSourceDialog({ registration, suggested }),
    [],
  );

  const handleSourceChanged = (result: SourceChangeResult) => {
    setDashboard((current) => {
      if (!current) return current;
      return {
        ...current,
        registrations: current.registrations.map((reg) =>
          reg.id === result.registration.id ? result.registration : reg,
        ),
        recentSyncLogs: [result.syncLog, ...current.recentSyncLogs].slice(
          0,
          50,
        ),
      };
    });
  };

  const refreshQuietly = useCallback(() => {
    void fetchDashboard(false);
  }, [fetchDashboard]);

  const getRowSyncDisabledReason = (registrationId: number): string | null => {
    if (!api?.apiConfigured) return "Bonnetid API-token er ikke konfigurert";
    if (api.circuitBreakerOpen)
      return "Circuit breaker er åpen — sync er pauset";
    if (isSyncing) return "Full sync kjører allerede";
    if (syncingId === registrationId) return "Synkroniserer...";
    return null;
  };

  const stats = dashboard?.stats;
  const api = dashboard?.apiStatus;

  const nf = (n: number | undefined) => (n ?? 0).toLocaleString("nb-NO");

  const registrations = (dashboard?.registrations ?? []).filter((reg) =>
    matchesSearch(search, [
      reg.mosqueName,
      reg.orgNr,
      reg.source,
      reg.sourceOrgNr,
    ]),
  );

  const registrationColumns: Array<Column<ExternalJamatRegistration>> = [
    {
      key: "name",
      header: "Moské",
      render: (reg) => <b>{reg.mosqueName || "—"}</b>,
    },
    {
      key: "orgNr",
      header: "Org.nr.",
      numeric: true,
      render: (reg) => <span className={styles.muted}>{reg.orgNr}</span>,
    },
    {
      key: "source",
      header: "Kilde",
      render: (reg) => (
        <>
          <Badge tone="blue">{reg.source}</Badge>
          {reg.sourceChanged && (
            <span
              className={styles.sourceNote}
              title="Tidene hentes fra en annen Bonnetid-ID enn moskeens org.nr."
            >
              fra {reg.sourceOrgNr}
            </span>
          )}
        </>
      ),
    },
    {
      key: "jamat",
      header: "Jamat",
      numeric: true,
      render: (reg) => nf(reg.bonnetidJamatDays),
    },
    {
      key: "jummah",
      header: "Jummah",
      numeric: true,
      render: (reg) => nf(reg.bonnetidJummahRows),
    },
    {
      key: "first",
      header: "Første sync",
      numeric: true,
      render: (reg) => (
        <span className={styles.muted}>
          {formatDateTime(reg.firstSyncedAt)}
        </span>
      ),
    },
    {
      key: "last",
      header: "Siste sync",
      numeric: true,
      render: (reg) => (
        <span
          className={styles.muted}
          title="Siste nattsynk eller manuelle synk"
        >
          {formatDateTime(reg.lastSyncedAt)}
        </span>
      ),
    },
    {
      key: "lazy",
      header: "Sist synket ved bruk",
      numeric: true,
      render: (reg) => (
        <span
          className={styles.muted}
          title="Når noen åpnet moskeen og jamat- eller jummah-tidene manglet. Ga ikke Bonnetid tidene, spør vi ikke igjen før neste nattsynk."
        >
          {reg.lastLazySyncedAt ? formatDateTime(reg.lastLazySyncedAt) : "—"}
        </span>
      ),
    },
    {
      key: "cron",
      header: "Cron",
      render: (reg) => (
        <Toggle
          id={`active-${reg.id}`}
          checked={reg.isActive}
          disabled={togglingId === reg.id || syncingId === reg.id}
          onChange={() => handleToggleActive(reg)}
          label={
            <span className={styles.muted}>
              {reg.isActive ? "Aktiv" : "Pauset"}
            </span>
          }
        />
      ),
    },
    {
      key: "sync",
      header: "Sync",
      render: (reg) => {
        const syncDisabledReason = getRowSyncDisabledReason(reg.id);
        return (
          <div className={styles.rowActions}>
            <Button
              variant="secondary"
              size="sm"
              icon={<Icon name="swap" size={14} />}
              loading={syncingId === reg.id}
              disabled={syncDisabledReason !== null}
              onClick={() => handleSyncRegistration(reg)}
              title={
                syncDisabledReason ??
                `Synkroniser ${reg.mosqueName || reg.orgNr} fra Bonnetid`
              }
            >
              Sync
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<Icon name="link" size={14} />}
              disabled={syncingId === reg.id}
              onClick={() => openSourceChange(reg)}
              title="Hent tidene fra en annen Bonnetid-kilde. Moskeen beholder brukere, push og admins i iBønn."
            >
              Koble til annen kilde
            </Button>
            {lastRowLog[reg.id] && (
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="doc" size={14} />}
                onClick={() =>
                  setDetailsLog({
                    id: lastRowLog[reg.id],
                    title: `Hva synken hentet for ${reg.mosqueName || reg.orgNr}`,
                  })
                }
              >
                Vis hva som ble hentet
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const logColumns: Array<Column<ExternalSyncLog>> = [
    {
      key: "time",
      header: "Tid",
      numeric: true,
      render: (log) => (
        <span className={styles.muted}>{formatDateTime(log.startedAt)}</span>
      ),
    },
    {
      key: "mode",
      header: "Modus",
      render: (log) => (
        <Badge
          tone={
            log.mode === "CRON"
              ? "grey"
              : log.mode === "MANUAL_SINGLE"
                ? "blue"
                : "lilac"
          }
        >
          {log.mode === "MANUAL_SINGLE"
            ? "MANUELL (1)"
            : log.mode === "SOURCE_CHANGE"
              ? "KILDE BYTTET"
              : log.mode}
        </Badge>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (log) => (
        <Badge tone={log.success ? "mint" : "rose"}>
          {log.success ? "OK" : "Med feil"}
        </Badge>
      ),
    },
    {
      key: "mosques",
      header: "Moskeer",
      render: (log) => (
        <>
          {log.mosquesProcessed}
          {log.mosquesSkipped > 0 && (
            <span className={styles.muted}>
              {" "}
              (+{log.mosquesSkipped} hoppet over)
            </span>
          )}
        </>
      ),
    },
    {
      key: "jamat",
      header: "Jamat",
      render: (log) => (
        <>
          +{log.jamatDaysCreated}
          {log.jamatDaysSkipped > 0 && (
            <span className={styles.muted}>
              {" "}
              ({log.jamatDaysSkipped} eksisterte)
            </span>
          )}
        </>
      ),
    },
    {
      key: "jummah",
      header: "Jummah",
      render: (log) => (
        <>
          +{log.jummahRowsCreated}
          {log.jummahRowsReplaced > 0 && (
            <span className={styles.muted}>
              {" "}
              ({log.jummahRowsReplaced} erstattet)
            </span>
          )}
        </>
      ),
    },
    {
      key: "details",
      header: "",
      render: (log) =>
        log.hasDetails ? (
          <Button
            variant="ghost"
            size="sm"
            icon={<Icon name="doc" size={14} />}
            onClick={() =>
              setDetailsLog({ id: log.id, title: "Hva synken hentet" })
            }
          >
            Detaljer
          </Button>
        ) : null,
    },
    {
      key: "errors",
      header: "Merknad / feil",
      render: (log) => (
        <>
          {log.note && <span className={styles.sourceNote}>{log.note}</span>}
          {log.errors.length === 0 ? (
            !log.note && <span className={styles.muted}>–</span>
          ) : (
            <ul className={styles.errorList}>
              {log.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          )}
        </>
      ),
    },
  ];

  const statusRows: Array<{
    label: string;
    ok: boolean | undefined;
    warn?: boolean;
    text: React.ReactNode;
  }> = [
    {
      label: "Bonnetid API",
      ok: api?.apiConfigured,
      text: api?.apiConfigured ? "Token konfigurert" : "Mangler token",
    },
    {
      label: "Cron-jobb",
      ok: api?.cronEnabled,
      warn: !api?.cronEnabled,
      text: (
        <>
          {api?.cronEnabled ? "Aktiv" : "Deaktivert"}
          {api?.cronSchedule && (
            <span className={styles.muted}> · {api.cronSchedule}</span>
          )}
        </>
      ),
    },
    {
      label: "Circuit breaker",
      ok: !api?.circuitBreakerOpen,
      text: (
        <>
          {api?.circuitBreakerOpen ? "Åpen (pauset)" : "Lukket (OK)"}
          {api?.circuitBreakerOpen && api.circuitBreakerOpenUntil && (
            <span className={styles.muted}>
              {" "}
              · til {formatDateTime(api.circuitBreakerOpenUntil)}
            </span>
          )}
          {!api?.circuitBreakerOpen && api && (
            <span className={styles.muted}>
              {" "}
              · {api.circuitBreakerFailures} feil på rad
            </span>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Eksterne kilder"
        subtitle="Eksterne jamat-kilder, synkronisering og cron-logg."
        actions={
          DASHBOARD_TABS.includes(activeTab) && (
            <>
              <Button
                variant="secondary"
                icon={<Icon name="swap" size={16} />}
                onClick={() => fetchDashboard()}
                loading={isLoading}
                disabled={isLoading || isSyncing}
              >
                Oppdater
              </Button>
              <Button
                icon={<Icon name="spark" size={16} />}
                onClick={handleSync}
                loading={isSyncing}
                disabled={isLoading || isSyncing || api?.circuitBreakerOpen}
              >
                {isSyncing ? "Synkroniserer …" : "Kjør sync nå"}
              </Button>
            </>
          )
        }
      />

      <Tabs
        label="Eksterne kilder"
        className={styles.pageTabs}
        value={activeTab}
        onChange={setActiveTab}
        tabs={TABS.map((t) => ({
          value: t.value,
          label: t.label,
          extra:
            t.value === "moskeer" && dashboard ? (
              <Badge tone="grey">{nf(dashboard.registrations.length)}</Badge>
            ) : undefined,
        }))}
      />

      {activeTab === "mymasjid" ? (
        <MyMasjidExternalTab />
      ) : activeTab === "radata" ? (
        <BonnetidRawPanel />
      ) : activeTab === "import" ? (
        <BonnetidImportPanel />
      ) : isLoading && !dashboard ? (
        <>
          <div className={styles.statsGrid}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} height={84} radius={18} />
            ))}
          </div>
          <Skeleton height={180} radius={20} />
        </>
      ) : !dashboard ? (
        <Card>
          <EmptyState icon="alert" title="Kunne ikke laste oversikten">
            Prøv å oppdatere.
          </EmptyState>
        </Card>
      ) : activeTab === "oversikt" ? (
        <>
          <div className={styles.statsGrid}>
            <div className={styles.stat}>
              <b>{nf(stats?.registeredMosques)}</b>
              <span>Registrerte moskeer</span>
            </div>
            <div className={styles.stat}>
              <b>{nf(stats?.activeRegistrations)}</b>
              <span>Aktive i cron-sync</span>
            </div>
            <div className={styles.stat}>
              <b>{nf(stats?.totalBonnetidJamatDays)}</b>
              <span>Jamat-dager fra Bonnetid</span>
            </div>
            <div className={styles.stat}>
              <b>{nf(stats?.totalBonnetidJummahRows)}</b>
              <span>Jummah-rader fra Bonnetid</span>
            </div>
            <div className={styles.stat}>
              <b>{nf(stats?.totalAdminJamatDays)}</b>
              <span>Admin jamat-dager</span>
            </div>
          </div>

          <Card title="API og systemstatus">
            <div className={styles.statusList}>
              {statusRows.map((row) => (
                <div key={row.label} className={styles.statusRow}>
                  <span
                    className={
                      row.ok
                        ? styles.dotOk
                        : row.warn
                          ? styles.dotOff
                          : styles.dotErr
                    }
                    aria-hidden="true"
                  />
                  <b className={styles.statusLabel}>{row.label}</b>
                  <span className={styles.statusText}>{row.text}</span>
                </div>
              ))}
            </div>
            <p className={styles.muted}>
              Nattsynken kjører kl. 02:00. Når noen åpner en moské og jamat-
              eller jummah-tidene mangler, hentes moskeen ved bruk (maks hvert
              15. minutt). Gir ikke Bonnetid tidene, spør vi ikke igjen før
              neste nattsynk.
            </p>
          </Card>
        </>
      ) : activeTab === "moskeer" ? (
        <Card title="Moskeer med Bonnetid som kilde">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Søk etter moské eller org.nr. …"
          />
          <Table
            columns={registrationColumns}
            rows={registrations}
            rowKey={(reg) => reg.id}
            empty={
              search.trim() ? (
                <EmptyState icon="search" title="Ingen treff">
                  Ingen moskeer matcher «{search.trim()}».
                </EmptyState>
              ) : (
                <EmptyState icon="link" title="Ingen moskeer ennå">
                  Første synkronisering fra Bonnetid legger moskeen inn
                  automatisk.
                </EmptyState>
              )
            }
          />
        </Card>
      ) : activeTab === "verktoy" ? (
        <BonnetidToolsPanel
          registrations={dashboard.registrations}
          onChangeSource={openSourceChange}
          onChanged={refreshQuietly}
        />
      ) : (
        <Card title="Synkroniseringslogg">
          <Table
            columns={logColumns}
            rows={dashboard.recentSyncLogs}
            rowKey={(log) => log.id}
            empty={
              <EmptyState icon="doc" title="Ingen synkroniseringslogger ennå" />
            }
          />
        </Card>
      )}

      <SyncDetailsDialog
        logId={detailsLog?.id ?? null}
        title={detailsLog?.title}
        onClose={() => setDetailsLog(null)}
      />

      <SourceChangeDialog
        registration={sourceDialog?.registration ?? null}
        suggestedSource={sourceDialog?.suggested}
        onClose={() => setSourceDialog(null)}
        onChanged={handleSourceChanged}
      />
    </>
  );
};

export default ExternalPage;
